/**
 * Marks every icon element non-translatable, so a page translation cannot turn icons into words.
 *
 * `<tds-icon>` renders a Material Symbols ligature *name* as a text node ("close", "search"), which
 * the font draws as a glyph. A translator sees ordinary English words and rewrites them, the
 * ligature stops matching, and the raw word is painted where the icon should be.
 *
 * The `notranslate` meta tag in `index.html` only stops Chrome *offering* to translate — picking
 * "Translate to French" from the menu overrides it. The `translate` attribute is the part Chrome
 * honours even then, and it is inherited, so setting it on the host covers the text node inside.
 *
 * This is done from the DOM rather than from a directive on purpose: TDS renders icons inside its
 * own components (the notification status icon, the combobox chevron, the modal close button), and
 * an app-level directive can never reach those.
 */

/** Every element whose text content is a ligature name rather than prose. */
const ICON_SELECTOR = 'tds-icon,.material-symbols-rounded';

function protect(root: ParentNode): void {
  if (root instanceof Element && root.matches(ICON_SELECTOR)) {
    root.setAttribute('translate', 'no');
  }
  for (const icon of root.querySelectorAll(ICON_SELECTOR)) {
    icon.setAttribute('translate', 'no');
  }
}

export function guardIconsFromTranslation() {
  return (): void => {
    protect(document.body);

    // Icons are added continuously as the user navigates, so the guard has to outlive bootstrap.
    // Registering here — before the first view renders — means this observer is called ahead of a
    // translator's own observer for any node added later, since callbacks run in registration order.
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            protect(node as Element);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };
}
